import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Optional,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";

/**
 * Radio group + radio. The group is the form control (implements
 * ControlValueAccessor). Children register themselves through DI so
 * the group can mark the matching one selected.
 *
 *   <bv-radio-group [(ngModel)]="size">
 *     <bv-radio value="sm">Small</bv-radio>
 *     <bv-radio value="md">Medium</bv-radio>
 *     <bv-radio value="lg">Large</bv-radio>
 *   </bv-radio-group>
 */
@Component({
    selector: "bv-radio-group",
    template: "<ng-content></ng-content>",
    styles: [
        `
      :host {
        display: flex;
        flex-direction: column;
        gap: var(--space-2);
      }
      :host.bv-radio-group--horizontal {
        flex-direction: row;
        flex-wrap: wrap;
        gap: var(--space-4);
      }
      @media (min-width: 640px) {
        :host.bv-radio-group--responsive {
          flex-direction: row;
          flex-wrap: wrap;
          gap: var(--space-4);
        }
      }
    `,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => BvRadioGroupComponent),
            multi: true,
        },
    ],
    host: {
        class: "bv-radio-group",
        role: "radiogroup",
        "[class.bv-radio-group--horizontal]": "orientation === 'horizontal'",
        "[class.bv-radio-group--responsive]": "orientation === 'responsive'",
    }
})
export class BvRadioGroupComponent implements ControlValueAccessor {
  /**
   * `vertical` (default): column, tight gap.
   * `horizontal`: row with wrap, breathable gap.
   * `responsive`: column below 640px, row above. The 640px threshold
   *               matches `sm` in this codebase's existing media queries.
   */
  @Input() orientation: "vertical" | "horizontal" | "responsive" = "vertical";

  value: unknown = null;
  disabled = false;

  private children: BvRadioComponent[] = [];
  private onChange: (v: unknown) => void = () => {};
  private onTouched: () => void = () => {};

  register(c: BvRadioComponent): void {
    this.children.push(c);
    c.setSelected(c.value === this.value);
    c.setGroupDisabled(this.disabled);
  }

  unregister(c: BvRadioComponent): void {
    this.children = this.children.filter((x) => x !== c);
  }

  selectValue(value: unknown): void {
    if (this.disabled) return;
    this.value = value;
    this.onChange(value);
    this.onTouched();
    this.refreshChildren();
  }

  private refreshChildren(): void {
    this.children.forEach((c) => c.setSelected(c.value === this.value));
  }

  writeValue(value: unknown): void {
    this.value = value;
    this.refreshChildren();
  }
  registerOnChange(fn: (v: unknown) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
    this.children.forEach((c) => c.setGroupDisabled(disabled));
  }
}

@Component({
    selector: "bv-radio",
    templateUrl: "./radio.component.html",
    styleUrls: ["./radio.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: "bv-radio",
        role: "radio",
        "[attr.aria-checked]": "selected",
        "[attr.aria-disabled]": "isDisabled || null",
        "[attr.tabindex]": "isDisabled ? -1 : 0",
        "[class.bv-radio--selected]": "selected",
        "[class.bv-radio--disabled]": "isDisabled",
    }
})
export class BvRadioComponent implements OnInit, OnDestroy {
  @Input() value: unknown = null;
  @Input() disabled = false;

  selected = false;
  groupDisabled = false;

  constructor(
    @Optional() private group: BvRadioGroupComponent | null,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.group?.register(this);
  }

  ngOnDestroy(): void {
    this.group?.unregister(this);
  }

  get isDisabled(): boolean {
    return this.disabled || this.groupDisabled;
  }

  setSelected(selected: boolean): void {
    if (this.selected === selected) return;
    this.selected = selected;
    this.cdr.markForCheck();
  }

  setGroupDisabled(disabled: boolean): void {
    this.groupDisabled = disabled;
    this.cdr.markForCheck();
  }

  @HostListener("click", ["$event"])
  onClick(event: Event): void {
    this.activate(event);
  }

  @HostListener("keydown.space", ["$event"])
  @HostListener("keydown.enter", ["$event"])
  onKeydown(event: Event): void {
    this.activate(event);
  }

  private activate(event: Event): void {
    if (this.isDisabled) return;
    event.preventDefault();
    this.group?.selectValue(this.value);
  }
}
